/**
 * Migration Service
 *
 * Reads each entity's migration status and says where the app should read and
 * write that entity. Legacy (Java) owns an entity until its status moves on:
 * - not_started, syncing, or no row: read and write legacy
 * - soaking: read Erebus, still write legacy
 * - cutover, complete: read and write Erebus
 */

import { erebusHttp } from './instances';
import type { MigrationState, MigrationStatus } from './types';

/** The entity name the backend uses for tenants (EntityTenant in pkg/migration/state). */
export const TENANT_ENTITY = 'tenant';

/** The entity name the backend uses for employees (EntityEmployee in pkg/migration/state). */
export const EMPLOYEE_ENTITY = 'employee';

/** The entity name the backend uses for tenant locations (EntityLocation in pkg/migration/state). */
export const LOCATION_ENTITY = 'location';

/** The entity name the backend uses for tenant contacts (EntityTenantContact in pkg/migration/state). */
export const TENANT_CONTACT_ENTITY = 'tenant-contact';

const erebusReadStatuses: readonly MigrationStatus[] = ['soaking', 'cutover', 'complete'];
const erebusWriteStatuses: readonly MigrationStatus[] = ['cutover', 'complete'];

/** Returns the status of every entity with a state row. Open to any signed-in user. */
export async function getMigrationStates(): Promise<MigrationState[]> {
  const { data } = await erebusHttp.get<MigrationState[]>('/migration/states');
  return data;
}

/** The entity's status, or undefined when it has no row (legacy owns it). */
function statusOf(states: MigrationState[], entity: string): MigrationStatus | undefined {
  return states.find((s) => s.entity === entity)?.status;
}

/** True when reads of the entity should go to Erebus. False means legacy. */
export function readsFromErebus(states: MigrationState[], entity: string): boolean {
  const status = statusOf(states, entity);
  return status !== undefined && erebusReadStatuses.includes(status);
}

/** True when writes of the entity should go to Erebus. False means legacy. */
export function writesToErebus(states: MigrationState[], entity: string): boolean {
  const status = statusOf(states, entity);
  return status !== undefined && erebusWriteStatuses.includes(status);
}
